import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdOutlinePhone, MdLockOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { authAPI } from "services/api";
import { useAuth } from "context/AuthContext";

const countries = [
    { name: "Türkiye", code: "+90", flag: "🇹🇷", sample: "5xx xxx xxxx" },
    { name: "Almanya", code: "+49", flag: "🇩🇪", sample: "15x xxxx xxxx" },
    { name: "ABD", code: "+1", flag: "🇺🇸", sample: "201 xxx xxxx" },
    { name: "İngiltere", code: "+44", flag: "🇬🇧", sample: "7400 xxxxxx" },
    { name: "Rusya", code: "+7", flag: "🇷🇺", sample: "9xx xxx xxxx" },
    { name: "Suudi Arabistan", code: "+966", flag: "🇸🇦", sample: "5x xxx xxxx" },
];

const sanitizePhone = (v) => v.replace(/[^\d]/g, "").slice(0, 10);

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [formData, setFormData] = useState({ phone: "", password: "" });
    const [countryCode, setCountryCode] = useState("+90");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handlePhoneChange = (e) => setFormData({ ...formData, phone: sanitizePhone(e.target.value) });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (formData.phone.length !== 10 || !formData.phone.startsWith("5")) {
            setError("Lütfen geçerli bir Telefon Numarası giriniz (5xx ile başlayan 10 hane).");
            setLoading(false);
            return;
        }

        try {
            const fullPhone = countryCode + formData.phone;
            const response = await authAPI.login(fullPhone, formData.password);
            login(response.user, response.token);
            navigate(`/${response.user.role}/dashboard`);
        } catch (err) {
            setError(err.response?.data?.message || "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.");
        } finally {
            setLoading(false);
        }
    };

    const placeholder = countries.find((c) => c.code === countryCode)?.sample || "5xx xxx xxxx";

    return (
        <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
            <div className="mt-[5vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
                {/* Başlık Alanı */}
                <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">Hoş Geldiniz</h4>
                <p className="mb-9 ml-1 text-base text-gray-600 dark:text-gray-400">
                    Hesabınıza giriş yapın ve randevularınızı yönetin.
                </p>

                {/* Hata Mesajı */}
                {error && (
                    <div className="mb-4 flex items-center rounded-xl bg-red-100 p-3 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        <span className="mr-2 text-lg">⚠️</span> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="w-full">
                    {/* Telefon Numarası Alanı */}
                    <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">
                        Telefon Numarası
                    </label>
                    <div className="mb-4 flex gap-3">
                        {/* Custom Tailwind Select */}
                        <div className="relative w-[130px]">
                            <select
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                disabled={loading}
                                className="flex h-12 w-full appearance-none items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:bg-navy-900 dark:hover:border-white/20"
                            >
                                {countries.map((c) => (
                                    <option key={c.code} value={c.code} className="dark:bg-navy-900">
                                        {c.flag} {c.code}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>

                        {/* Telefon Input */}
                        <div className="relative flex-1">
                            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                                <MdOutlinePhone className="h-5 w-5" />
                            </div>
                            <input
                                type="tel"
                                name="phone"
                                placeholder={placeholder}
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                disabled={loading}
                                required
                                className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                            />
                        </div>
                    </div>

                    {/* Şifre Alanı */}
                    <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Şifre</label>
                    <div className="relative mb-6">
                        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                            <MdLockOutline className="h-5 w-5" />
                        </div>
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={loading}
                            required
                            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-10 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                        />
                        <div
                            className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-400 hover:text-brand-500"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                        </div>
                    </div>

                    {/* Aksiyonlar */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="linear mt-2 flex w-full items-center justify-center rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                    >
                        {loading ? (
                            <svg className="mr-2 h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            "Giriş Yap"
                        )}
                    </button>

                    <div className="mt-6 flex flex-col items-center gap-3">
                        <Link to="/auth/fast-login" className="flex items-center text-sm font-bold text-green-500 transition-colors hover:text-green-600 dark:text-green-400">
                            <span className="mr-1 text-lg">⚡</span> Hızlı Giriş Yap
                        </Link>

                        <div className="mt-2 flex items-center gap-1 text-sm font-medium text-navy-700 dark:text-gray-400">
                            Hesabınız yok mu?
                            <Link to="/auth/register" className="text-brand-500 hover:text-brand-600 dark:text-brand-400">
                                Kayıt olun
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}